import classNames from 'classnames';
import * as React from 'react';
import { ComponentProps, RefObject } from 'react';

import { CollapsableProvider } from '../../hooks/ui/useCollapsable';
import borderRadiusAsClasses from '../../shared/borderRadiusAsClasses';
import PaddedBox from '../PaddedBox/PaddedBox';

import CardContent from './CardContent';
import CardHeader from './CardHeader';
import borderRadiusForCard from './helpers/borderRadiusForCard';

import styles from './Card.module.css';

type CardProps = {
    className?: string;
    collapsable?: boolean;
    defaultCollapsed?: boolean;
    children?: React.ReactNode | React.ReactNode[];
    variant?: ComponentProps<typeof PaddedBox>['variant'];
    horizontalPadding?: ComponentProps<typeof PaddedBox>['horizontalPadding'];
    verticalPadding?: ComponentProps<typeof PaddedBox>['verticalPadding'];
    fullHeight?: boolean;
    decorate?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    noShadow?: boolean;
    id?: string;
    cardRef?: RefObject<HTMLDivElement>;
    onExpand?: () => void;
    onCollapse?: () => void;
};

const Card = ({
    children,
    defaultCollapsed,
    collapsable = false,
    className,
    horizontalPadding = 'large',
    verticalPadding = 'large',
    variant = 'white',
    fullHeight = false,
    decorate = false,
    isFirst = true,
    isLast = true,
    noShadow = false,
    id,
    cardRef,
    onExpand,
    onCollapse,
}: CardProps) => {
    const borderRadius = borderRadiusForCard(isFirst, isLast);

    const classes = classNames(
        className,
        styles.Root,
        borderRadiusAsClasses(borderRadius, {
            size: 'large',
            before: true,
        }),
        {
            [styles.last]: isLast,
            [styles.noShadow]: noShadow,
            [styles.fullHeight]: fullHeight,
        }
    );

    return (
        <div className={classes} id={id} ref={cardRef}>
            <PaddedBox
                horizontalPadding={horizontalPadding}
                verticalPadding={verticalPadding}
                variant={variant}
                borderRadius={borderRadius}
                className={classNames(styles.ContentWrapper, {
                    [styles.ContentWrapper_fullHeight]: fullHeight,
                })}
            >
                {decorate && (
                    <span
                        className={classNames(
                            styles.Decoration,
                            variant === 'brand' && styles.Decoration_brand
                        )}
                    />
                )}
                <div className={styles.Content}>
                    <CollapsableProvider
                        defaultCollapsed={defaultCollapsed}
                        disabled={!collapsable}
                        onChange={(collapsed) => {
                            if (collapsed && onCollapse) {
                                onCollapse?.();
                            }

                            if (!collapsed && onExpand) {
                                onExpand?.();
                            }
                        }}
                    >
                        {children}
                    </CollapsableProvider>
                </div>
            </PaddedBox>
        </div>
    );
};

Card.Content = CardContent;
Card.Header = CardHeader;

export default Card;
